import { useMemo } from 'react'
import { ArrayParam, QueryParamConfig, StringParam, useQueryParam, useQueryParams } from 'use-query-params'
import { useTranslation } from 'react-i18next'

import { useBreadcrumbs } from '../../layout/breadcrumbsContext/useBreadcrumbs/useBreadcrumbs'

import { ActivityTagsTable } from './ActivityTagsTable/ActivityTagsTable'
import { OpportunitiesTable } from './OpportunitiesTable/OpportunitiesTable'
import { TimeLogsTable } from './TimeLogsTable/TimeLogsTable'
import {
  POSTHOG_FEATURE_FLAGS,
  useDidMountEffect,
  usePostHogFeatureFlag,
  useQueryList
} from '@percent/partner-dashboard/common/hooks'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import {
  OpportunityStatus,
  OpportunityVisibility,
  VolunteeringOpportunitySortField
} from '@percent/partner-dashboard/services/workplaceGiving/workplaceGivingService.types'
import { PartnerConfigProvider } from '@percent/partner-dashboard/context/config/PartnerConfigProvider'
import { PartnerConfigKey } from '@percent/partner-dashboard/context/config/PartnerConfigProvider.types'
import { Flex, Tab, TableColumnSortDirection, Tabs } from '@percent/lemonade'
import { TrueFalseBooleanParam } from '@percent/partner-dashboard/common/queryParams/queryParams'

enum VolunteeringTabs {
  OPPORTUNITIES = 'opportunities',
  TIME_LOGS = 'time_logs',
  ACTIVITY_TAGS = 'activity_tags'
}

export function VolunteeringContainer() {
  const { workplaceGivingService } = useServices()
  const { t } = useTranslation()
  const [tabQP, setTabQP] = useQueryParam('tab', StringParam)
  const isDraftsEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.PD_OPPORTUNITY_DRAFTS)

  const initialFilters = useMemo(
    () => ({
      parentId: 'null',
      status: isDraftsEnabled
        ? [OpportunityStatus.DRAFT, OpportunityStatus.ACTIVE, OpportunityStatus.CANCELLED, OpportunityStatus.CLOSED]
        : [OpportunityStatus.ACTIVE, OpportunityStatus.CANCELLED, OpportunityStatus.CLOSED]
    }),
    [isDraftsEnabled]
  )

  useBreadcrumbs([
    {
      label: t('breadcrumb.volunteering')
    }
  ])

  const tabs = useMemo(
    () => [
      { label: t('typography.opportunities'), key: VolunteeringTabs.OPPORTUNITIES },
      { label: t('typography.timeLogs'), key: VolunteeringTabs.TIME_LOGS },
      { label: t('typography.activityTags'), key: VolunteeringTabs.ACTIVITY_TAGS }
    ],
    [t]
  )

  const activeTab = useMemo(() => {
    if (!tabQP) return tabs[0].key

    return tabs.find(tab => tab.key === tabQP)?.key ?? tabs[0].key
  }, [tabs, tabQP])
  const [queryParams, setQueryParams] = useQueryParams(
    {
      query: StringParam,
      parentId: StringParam,
      status: ArrayParam,
      availableOnly: TrueFalseBooleanParam,
      visibility: ArrayParam,
      sort: StringParam as QueryParamConfig<VolunteeringOpportunitySortField | null | undefined>,
      direction: StringParam as QueryParamConfig<TableColumnSortDirection | null | undefined>
    },
    {}
  )

  const toRequestParams = () => ({
    ...initialFilters,
    status: queryParams.status?.length ? queryParams.status : initialFilters.status,
    query: queryParams.query?.length ? queryParams.query : null,
    ...(queryParams.sort ? { sort: queryParams.sort } : {}),
    ...(queryParams.direction ? { direction: queryParams.direction } : {}),
    ...(queryParams.availableOnly ? { availableOnly: true } : {}),
    ...(queryParams.visibility?.length ? { visibility: queryParams.visibility as OpportunityVisibility[] } : {})
  })

  const [{ data, isLoading, errorMessage, totalResults }, { query, nextPage, previousPage, refresh }] = useQueryList(
    workplaceGivingService.getVolunteeringOpportunitiesList,
    toRequestParams()
  )

  useDidMountEffect(() => {
    const base = { ...toRequestParams(), parentId: 'null' as const }

    if (!queryParams.parentId && !queryParams.status) {
      query(base)
      setQueryParams?.({
        ...queryParams,
        ...initialFilters
      })
    } else {
      query(base)
    }
  }, [queryParams, query])

  useDidMountEffect(() => {
    setQueryParams?.({
      ...queryParams,
      ...initialFilters,
      status: queryParams.status?.length ? queryParams.status : initialFilters.status,
      query: queryParams.query?.length ? queryParams.query : null,
      visibility: queryParams.visibility
    })
  }, [])

  const opportunitiesTable = (
    <OpportunitiesTable
      data={data}
      isLoading={isLoading}
      totalResults={totalResults}
      errorMessage={errorMessage}
      nextPage={nextPage}
      previousPage={previousPage}
      queryParams={queryParams}
      setQueryParams={setQueryParams}
      refresh={refresh}
    />
  )

  return (
    <PartnerConfigProvider partnerConfigKey={PartnerConfigKey.PREFERRED_COUNTRIES}>
      <Flex direction="column" bg="white" style={{ borderRadius: '8px' }}>
        <Tabs items={tabs} activeTab={activeTab} onChangeTab={setTabQP} />
        <Tab index={VolunteeringTabs.OPPORTUNITIES} value={activeTab}>
          {opportunitiesTable}
        </Tab>
        <Tab index={VolunteeringTabs.TIME_LOGS} value={activeTab}>
          <TimeLogsTable key={activeTab} />
        </Tab>
        <Tab index={VolunteeringTabs.ACTIVITY_TAGS} value={activeTab}>
          <ActivityTagsTable />
        </Tab>
      </Flex>
    </PartnerConfigProvider>
  )
}
